<template>
  <div class="bg-grey-lighten-4 min-h-screen">
    <v-progress-linear v-if="loading" indeterminate color="primary" absolute top style="z-index: 100" />
  
    <v-main style="flex: 1 1 auto; overflow-y: auto;">
      <v-container class="pa-4">
        <div class="mb-2">
          <v-btn variant="text" prepend-icon="mdi-arrow-left" color="grey-darken-2" class="px-0" :ripple="false"
            @click="goBack">
            Back to Mutual Funds
          </v-btn>
        </div>
        <div class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between mb-6">
          <div>
            <h2 class="text-h5 font-weight-bold text-indigo-darken-4">Portfolio Summary & History</h2>
            <div class="text-body-2 text-grey-darken-1 mt-1">Track your mutual fund performance</div>
          </div>
  
          <div class="text-md-right mt-4 mt-md-0 bg-white pa-4 rounded-xl elevation-1" style="min-width: 250px">
            <div class="text-caption text-grey-darken-1 font-weight-bold text-uppercase ls-1">Current Value</div>
            <div class="text-h4 font-weight-bold text-indigo-darken-4 mt-1">
              {{ formatINR(totalPortfolioValue) }}
            </div>
  
            <div v-if="totalPortfolioValue > 0" class="d-flex align-center justify-md-end mt-2">
              <div class="d-flex flex-column align-end mr-4">
                <span class="text-caption text-grey mb-0">Portfolio 1D</span>
                <span :class="totalDayGain >= 0 ? 'text-green-darken-1' : 'text-error'"
                  class="font-weight-bold text-body-2">
                  {{ totalDayGain >= 0 ? '+' : '' }}{{ formatINR(totalDayGain) }}
                  <span class="ml-1">({{ totalDayGainPerc }}%)</span>
                </span>
              </div>
  
              <div class="bg-grey-lighten-2 mx-2" style="width: 1px; height: 24px;"></div>
  
              <div class="d-flex flex-column align-end ml-2">
                <span class="text-caption text-grey mb-0">Nifty 1D</span>
                <span :class="niftyDayGain >= 0 ? 'text-green-darken-1' : 'text-error'"
                  class="font-weight-bold text-body-2">
                  {{ niftyDayGain >= 0 ? '+' : '' }}{{ niftyDayGain.toFixed(2) }}
                  <span class="ml-1">({{ niftyDayGainPerc }}%)</span>
                </span>
              </div>
            </div>
          </div>
        </div>
  
        <v-card class="mb-6 rounded-xl mx-auto" max-width="1200" elevation="2">
          <v-card-title class="text-h6 font-weight-bold pa-6 bg-grey-lighten-5 border-b">Account Value
            Breakdown</v-card-title>
          <v-card-text class="pa-6">
            <v-row>
              <v-col cols="12" md="7">
                <v-data-table :headers="accountHeaders" :items="accountData" :items-per-page="-1" class="elevation-0"
                  hide-default-footer>
                  <template v-slot:[`item.value`]="{ item }">
                    <div class="font-weight-bold">{{ formatINR(item.value) }}</div>
                  </template>
  
                  <template v-slot:[`item.dayGain`]="{ item }">
                    <div v-if="item.dayGain !== undefined">
                      <span :class="item.dayGain >= 0 ? 'text-green-darken-1' : 'text-error'" class="font-weight-bold">
                        {{ item.dayGain >= 0 ? '+' : '' }}{{ formatINR(item.dayGain) }}
                      </span>
                      <div :class="item.dayGain >= 0 ? 'text-green-darken-1' : 'text-error'" class="text-caption">
                        ({{ item.dayGainPerc?.toFixed(2) }}%)
                      </div>
                    </div>
                    <div v-else class="text-grey text-caption">N/A</div>
                  </template>
  
                  <template v-slot:[`item.share`]="{ item }">
                    <v-progress-linear :model-value="item.share * 100" color="indigo-lighten-1" height="8"
                      rounded></v-progress-linear>
                    <div class="text-caption text-right mt-1">{{ (item.share * 100).toFixed(1) }}%</div>
                  </template>
                </v-data-table>
              </v-col>
              <v-col cols="12" md="5" class="d-flex align-center justify-center">
                <v-chart class="chart" :option="donutChartOption" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card class="mb-6 rounded-xl mx-auto" max-width="1200" elevation="2">
          <v-card-title class="text-h6 font-weight-bold pa-6 bg-grey-lighten-5 border-b">
            Relative Performance (Growth %)
          </v-card-title>
          <v-card-text class="pa-6">
            <div class="d-flex justify-center mb-4">
              <v-chip color="primary" variant="outlined" class="font-weight-bold">
                Period: {{ selectedPeriod }}
              </v-chip>
            </div>
  
            <v-chart class="chart" :option="percentageChartOption" />
  
            <div class="text-caption text-grey text-center mt-2">
              * Compares percentage growth starting from 0% at the beginning of the selected period.
            </div>
          </v-card-text>
        </v-card>
        <v-card class="mb-6 rounded-xl mx-auto" max-width="1200" elevation="2">
          <v-card-title class="text-h6 font-weight-bold pa-6 bg-grey-lighten-5 border-b">Portfolio Value
            History</v-card-title>
          <v-card-text class="pa-6">
            <v-tabs v-model="selectedPeriod" bg-color="grey-lighten-4" color="primary" align-tabs="center"
              class="mb-4 rounded">
              <v-tab value="1M">1M</v-tab>
              <v-tab value="3M">3M</v-tab>
              <v-tab value="6M">6M</v-tab>
              <v-tab value="1Y">1Y</v-tab>
              <v-tab value="3Y">3Y</v-tab>
              <v-tab value="ALL">ALL</v-tab>
            </v-tabs>
            <v-chart class="chart" :option="lineChartOption" />
            <div class="d-flex justify-space-between mt-4 text-body-1 bg-grey-lighten-5 pa-4 rounded-lg">
              <div class="d-flex align-center">
                <span class="text-grey-darken-2 mr-2">Total Gain:</span>
                <span :class="totalGain >= 0 ? 'text-green-darken-1' : 'text-error'" class="font-weight-bold text-h6">
                  {{ formatINR(totalGain) }}
                </span>
              </div>
              <div class="d-flex align-center">
                <span class="text-grey-darken-2 mr-2">XIRR:</span>
                <span :class="xirr >= 0 ? 'text-green-darken-1' : 'text-error'" class="font-weight-bold text-h6">
                  {{ (xirr * 100).toFixed(2) }}%
                </span>
              </div>
            </div>
          </v-card-text>
        </v-card>
  
      </v-container>
    </v-main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, LineChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart from 'vue-echarts'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'

use([CanvasRenderer, PieChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent, DataZoomComponent])

const store = useStore()
const loading = ref(false)

const accountData = ref([])
const historyData = ref([])
const selectedPeriod = ref('1Y')
const totalGain = ref(0)
const xirr = ref(0)

const router = useRouter()

const goBack = () => {
  router.push('/mf')
}
// --- NEW: PERCENTAGE GROWTH CHART ---
const percentageChartOption = computed(() => {
  const period = selectedPeriod.value
  let filteredData = historyData.value
  const now = dayjs()

  // 1. Filter Data (Same logic as main chart)
  if (period !== 'ALL') {
    const subtractMap = { '1M': [1, 'month'], '3M': [3, 'month'], '6M': [6, 'month'], '1Y': [1, 'year'], '3Y': [3, 'year'] }
    if (subtractMap[period]) {
      filteredData = historyData.value.filter(item => dayjs(item.navDate).isAfter(now.subtract(...subtractMap[period])))
    }
  }

  if (filteredData.length === 0) return {}

  // 2. Prepare Clean Nifty Data (Remove 0s)
  let lastValidNifty = filteredData.find(d => d.niftyLevel > 0)?.niftyLevel || 1
  const cleanNiftyData = filteredData.map(item => {
    if (item.niftyLevel > 0) {
      lastValidNifty = item.niftyLevel
      return item.niftyLevel
    }
    return lastValidNifty
  })

  // 3. Determine Base Values (Start of the period)
  // We use the first data point of the selection as the baseline (0%)
  const basePortfolio = filteredData[0].value || 1
  const baseNifty = cleanNiftyData[0] || 1

  // 4. Calculate Percentage Change Series
  // Formula: ((Current - Base) / Base) * 100
  const portfolioSeries = filteredData.map(item => {
    return ((item.value - basePortfolio) / basePortfolio * 100).toFixed(2)
  })

  const niftySeries = cleanNiftyData.map(val => {
    return ((val - baseNifty) / baseNifty * 100).toFixed(2)
  })

  return {
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        let date = dayjs(params[0].name).format('DD MMM YYYY')
        let result = `<div class="font-weight-bold mb-1">${date}</div>`

        params.forEach(param => {
          // Add + sign for positive numbers
          const val = parseFloat(param.value)
          const colorClass = val >= 0 ? '#4CAF50' : '#FF5252' // Green/Red text for value
          const sign = val >= 0 ? '+' : ''

          result += `
            <div style="display:flex; justify-content:space-between; min-width:150px;">
              <span style="color:${param.color}">● ${param.seriesName}:</span>
              <span style="font-weight:bold; color:${colorClass}; margin-left:8px">${sign}${val}%</span>
            </div>`
        })
        return result
      }
    },
    legend: {
      data: ['Portfolio Growth %', 'Nifty Growth %'],
      bottom: 0
    },
    grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: filteredData.map(item => item.navDate),
      axisLabel: { formatter: val => dayjs(val).format('DD MMM') }
    },
    yAxis: {
      type: 'value',
      scale: true,
      axisLabel: { formatter: '{value}%' }, // Show % on axis
      splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } }
    },
    series: [
      {
        name: 'Portfolio Growth %',
        data: portfolioSeries,
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: { color: '#4CAF50', width: 2 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(76, 175, 80, 0.5)' }, { offset: 1, color: 'rgba(232, 245, 233, 0.1)' }]
          }
        }
      },
      {
        name: 'Nifty Growth %',
        data: niftySeries,
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: {
          color: '#FF5252',
          width: 2,
          type: 'dashed'
        }
      }
    ]
  }
})

const niftyDayGain = computed(() => {
  // Get data sorted by date (newest first)
  const sorted = [...historyData.value].sort((a, b) => dayjs(b.navDate).diff(dayjs(a.navDate)))

  // Find latest valid Nifty > 0
  const latest = sorted.find(item => item.niftyLevel > 0)
  if (!latest) return 0

  // Find previous valid Nifty > 0 by ensuring the value is actually different 
  // (This skips rolled-over holiday/weekend values)
  const previous = sorted.find(item => item.niftyLevel > 0 && item.niftyLevel !== latest.niftyLevel)

  if (!previous) return 0
  return latest.niftyLevel - previous.niftyLevel
})

const niftyDayGainPerc = computed(() => {
  const sorted = [...historyData.value].sort((a, b) => dayjs(b.navDate).diff(dayjs(a.navDate)))
  const latest = sorted.find(item => item.niftyLevel > 0)
  
  // Apply the same skip logic here
  const previous = sorted.find(item => item.niftyLevel > 0 && item.niftyLevel !== latest?.niftyLevel)

  if (!latest || !previous || previous.niftyLevel === 0) return '0.00'

  const gain = latest.niftyLevel - previous.niftyLevel
  return ((gain / previous.niftyLevel) * 100).toFixed(2)
})

// Computed properties for Top Header
const totalPortfolioValue = computed(() => {
  return accountData.value.reduce((sum, item) => sum + (item.value || 0), 0)
})

const totalDayGain = computed(() => {
  return accountData.value.reduce((sum, item) => sum + (item.dayGain || 0), 0)
})

const totalDayGainPerc = computed(() => {
  const previousValue = totalPortfolioValue.value - totalDayGain.value
  if (previousValue === 0) return '0.00'
  return ((totalDayGain.value / previousValue) * 100).toFixed(2)
})

const accountHeaders = [
  { title: 'Account Name', key: 'accountName', align: 'start', width: '25%' },
  { title: 'Value (₹)', key: 'value', align: 'end' },
  { title: '1D Gain', key: 'dayGain', align: 'end' },
  { title: 'Share', key: 'share', align: 'end', width: '25%' },
]

const donutChartOption = computed(() => {
  return {
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: '0%', left: 'center' },
    series: [
      {
        name: 'Account Value',
        type: 'pie',
        radius: ['40%', '65%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 10, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        data: accountData.value.map(item => ({ value: item.value, name: item.accountName }))
      }
    ]
  }
})

// --- UPDATED CHART OPTION ---
const lineChartOption = computed(() => {
  const period = selectedPeriod.value
  let filteredData = historyData.value
  const now = dayjs()

  // Filter by Date Period
  if (period !== 'ALL') {
    const subtractMap = { '1M': [1, 'month'], '3M': [3, 'month'], '6M': [6, 'month'], '1Y': [1, 'year'], '3Y': [3, 'year'] }
    if (subtractMap[period]) {
      filteredData = historyData.value.filter(item => dayjs(item.navDate).isAfter(now.subtract(...subtractMap[period])))
    }
  }

  // --- FIX: CLEAN NIFTY DATA (Remove 0s) ---
  // Create a clean array where 0s are replaced by the previous valid value
  // This prevents the chart axis from dropping to 0 and flattening the line
  let lastValidNifty = filteredData.find(d => d.niftyLevel > 0)?.niftyLevel || 0
  const cleanNiftyData = filteredData.map(item => {
    if (item.niftyLevel > 0) {
      lastValidNifty = item.niftyLevel
      return item.niftyLevel
    }
    return lastValidNifty // Use previous value if current is 0
  })

  return {
    tooltip: {
      trigger: 'axis',
      formatter: params => {
        let date = dayjs(params[0].name).format('DD MMM YYYY')
        let result = `<div class="font-weight-bold mb-1">${date}</div>`

        params.forEach(param => {
          const val = param.seriesName === 'Nifty'
            ? param.value.toFixed(2)
            : formatINR(param.value)

          result += `
            <div style="display:flex; justify-content:space-between; min-width:150px;">
              <span style="color:${param.color}">● ${param.seriesName}:</span>
              <span class="font-weight-bold ml-2">${val}</span>
            </div>`
        })
        return result
      }
    },
    legend: {
      data: ['Portfolio Value', 'Nifty'],
      bottom: 0
    },
    grid: { left: '3%', right: '3%', bottom: '10%', containLabel: true },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: filteredData.map(item => item.navDate),
      axisLabel: { formatter: val => dayjs(val).format('DD MMM') }
    },
    yAxis: [
      {
        type: 'value',
        name: 'Portfolio',
        position: 'left',
        scale: true, // Auto-scale min/max
        splitLine: { show: true, lineStyle: { type: 'dashed', color: '#eee' } },
        axisLabel: { formatter: val => val >= 10000000 ? '₹' + (val / 10000000).toFixed(2) + 'Cr' : '₹' + (val / 100000).toFixed(0) + 'L' }
      },
      {
        type: 'value',
        name: 'Nifty',
        position: 'right',
        scale: true, // This enables the "Zoomed in" view
        // Force the min to be based on data, not 0
        min: (value) => value.min - (value.max - value.min) * 0.05,
        splitLine: { show: false },
        axisLabel: { formatter: val => val.toFixed(0) },
        axisLine: { show: false }
      }
    ],
    series: [
      {
        name: 'Portfolio Value',
        data: filteredData.map(item => item.value),
        type: 'line',
        smooth: true,
        showSymbol: false,
        yAxisIndex: 0,
        lineStyle: { color: '#4CAF50', width: 2 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [{ offset: 0, color: 'rgba(76, 175, 80, 0.5)' }, { offset: 1, color: 'rgba(232, 245, 233, 0.1)' }]
          }
        }
      },
      {
        name: 'Nifty',
        // Use the cleaned data array
        data: cleanNiftyData,
        type: 'line',
        smooth: true,
        showSymbol: false,
        yAxisIndex: 1,
        lineStyle: {
          color: '#FF5252',
          width: 1.5,
          type: 'dashed'
        }
      }
    ]
  }
})
const formatINR = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(num || 0)

const fetchAccountData = async () => {
  try {
    const res = await store.dispatch('portfolio/getMfPortfolioValue')
    if (res && res.mfPortoflioValueList) {
      accountData.value = res.mfPortoflioValueList
    }
  } catch (err) {
    console.error("Failed to fetch account summary", err)
  }
}

const fetchHistoryData = async () => {
  loading.value = true
  try {
    const res = await store.dispatch('portfolio/getMfPortfolioValueHisory')

    if (res && res.mfPortfolioValueAccountInfoList) {
      const accounts = res.mfPortfolioValueAccountInfoList

      totalGain.value = accounts.reduce((sum, acc) => sum + (acc.totalGain || 0), 0)
      xirr.value = 0

      const globalDateMap = {}
      const globalNiftyMap = {} // New Map for Nifty
      const accountGains = {}

      accounts.forEach(account => {
        if (account.mfPortfolioValueHistoryList) {
          const accountDateMap = {}

          account.mfPortfolioValueHistoryList.forEach(item => {
            const date = item.navDate
            // Global Aggregate
            if (!globalDateMap[date]) globalDateMap[date] = 0
            globalDateMap[date] += item.value

            // Capture Nifty Level (Only need to do this once per date, but overwriting is fine)
            // Ensure we don't overwrite a valid value with null if multiple accounts exist
            if (item.niftyLevel) {
              globalNiftyMap[date] = item.niftyLevel
            }

            // Account Aggregate
            if (!accountDateMap[date]) accountDateMap[date] = 0
            accountDateMap[date] += item.value
          })

          // Calculate 1D Gain per account (existing logic)
          const sortedDates = Object.keys(accountDateMap).sort((a, b) => dayjs(b).diff(dayjs(a)))
          if (sortedDates.length >= 2) {
            const latestVal = accountDateMap[sortedDates[0]]
            const prevVal = accountDateMap[sortedDates[1]]
            const gain = latestVal - prevVal
            const gainPerc = prevVal !== 0 ? (gain / prevVal) * 100 : 0

            accountGains[account.accountName] = { dayGain: gain, dayGainPerc: gainPerc }
          }
        }
      })

      // Combine Data
      historyData.value = Object.keys(globalDateMap)
        .map(date => ({
          navDate: date,
          value: globalDateMap[date],
          niftyLevel: globalNiftyMap[date] || 0 // Add niftyLevel to the main data object
        }))
        .sort((a, b) => dayjs(a.navDate).diff(dayjs(b.navDate)))

      // Fallback if account API failed but history succeeded
      if (accountData.value.length === 0) {
        accountData.value = accounts.map(acc => ({
          accountName: acc.accountName,
          value: historyData.value[historyData.value.length - 1]?.value || 0,
          share: 0
        }))
      }

      // Merge gains into account table
      accountData.value = accountData.value.map(acc => {
        const gainInfo = accountGains[acc.accountName]
        if (gainInfo) return { ...acc, ...gainInfo }
        return acc
      })
    }
  } catch (err) {
    console.error("Failed to fetch history data", err)
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await fetchAccountData()
  await fetchHistoryData()
})
</script>
<style scoped>
.min-h-screen {
  min-height: 100vh;
}

.chart {
  height: 350px;
  width: 100%;
}

.ls-1 {
  letter-spacing: 1px;
}
</style>